import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, InputBase, makeStyles } from '@material-ui/core';
import { debounce, isFunction } from 'lodash';
import clearIcon from '../../static/images/clear.png';
import { SearchIcon } from '../Icon';

const useStyles = makeStyles((theme) => ({
  search: ({ width, height }) => ({
    borderRadius: '0.5rem',
    backgroundColor: theme.palette.font.white,
    width: width ?? '18.5rem',
    height: height ?? '2.375rem',
    display: 'flex',
    justifyContent: 'space-between',
    marginRight: '0.8rem',
    border: '0.0625rem solid #ddd',
  }),
  input: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.919rem',
    letterSpacing: '0',
    color: theme.palette.font.sub,
    fontWeight: '500',
    margin: '0 0.938rem 0 0.938rem',
    width: '100%',
  },
  iconbtn: {
    padding: '0',
    '&:hover': {
      backgroundColor: 'unset',
    },
  },
  icon: {
    backgroundColor: '#4c93ff',
    color: theme.palette.font.white,
    padding: '0.5rem 0.5rem 0.438rem 0.438rem',
    borderRadius: '0.4875rem',
    fontSize: '1.875rem',
    margin: '0.313rem 0.313rem 0.313rem 0',
  },
  closeIcon: {
    width: '1.5rem',
    height: '1.5rem',
  },
}));

const SearchInput = ({
  onClick,
  onChange,
  placeholder,
  inputID,
  buttonID,
  onFocus,
  onBlur,
  m,
  p,
  width,
  height,
  reset,
  clearID,
  closeIcon = false,
}) => {
  const classes = useStyles({ width, height });
  const [value, setValue] = useState('');

  // 重置數據
  useEffect(() => {
    setValue('');
  }, [reset]);

  // 防抖函數
  const debouncedSearch = useRef(
    debounce(async (inputValue) => {
      onChange(inputValue);
    }, 1000)
  ).current;

  // 清除副作用
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // input 的 onChange 事件
  const onChangeHandler = useCallback(
    (event) => {
      setValue(event.target.value);
      (async () => {
        await debouncedSearch(event.target.value);
      })();
    },
    [debouncedSearch]
  );

  // 處理 enter 事件
  const onKeyDownHandler = useCallback(
    (event) => {
      if ((event.code === 'Enter' || event.code === 'NumpadEnter') && isFunction(onClick)) {
        onClick(value);
      }
    },
    [onClick, value]
  );

  // 處理 onClick 事件
  const onClickHandler = useCallback(() => {
    onChange(value);
  }, [value]);

  // 處理 onClick 事件
  const onClearHandler = useCallback(() => {
    setValue('');
    const timeout = setTimeout(() => {
      onChange('');
      clearTimeout(timeout);
    }, 1000);
  }, []);

  return (
    <Box className={classes.search} m={m} p={p}>
      <InputBase
        className={classes.input}
        id={inputID}
        value={value}
        onChange={onChangeHandler}
        placeholder={placeholder}
        onKeyDown={onKeyDownHandler}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      <Box display="flex" gridColumnGap="0.5rem">
        {value && closeIcon && (
          <IconButton className={classes.iconbtn} id={clearID} onClick={onClearHandler}>
            <img className={classes.closeIcon} src={clearIcon} alt="clear" />
          </IconButton>
        )}
        <IconButton className={classes.iconbtn} id={buttonID} onClick={onClickHandler}>
          <SearchIcon className={classes.icon} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default SearchInput;
